class GiftWithPurchase {
  constructor () {
    this.onPushCartChange = this.onPushCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onPushCartChange,
    )

    if (this.data?.autoAddVariantId) {
      this._checkAutoAdd()
    }
  }

  async _checkAutoAdd () {
    if (!this.data?.autoAddVariantId || this._autoAddInflight) return
    const cart = await window.BAO.Utils.fetchJSON('/cart.js')
    if (!this.getCartIsValid(cart) || this._autoAddInflight) return
    const hasGwp = cart.items.some(item => item.properties?._gwp)
    if (!hasGwp) {
      this._autoAddInflight = true
      window.BAO.Cart.addItem({
        id: this.data.autoAddVariantId,
        quantity: 1,
        properties: { _gwp: 'true' },
      }).finally(() => {
        this._autoAddInflight = false
      })
    }
  }

  async onPushCartChange (event) {
    if (!this.shouldRun) return

    const eventState = event.detail?.state

    const freshPassedThreshold = eventState?.sections
      ? this.getPassedThresholdFromSections(eventState.sections)
      : null

    if (eventState?.items && eventState?.sections) {
      const gwpKeys = eventState.items
        .filter(item => item.properties?._gwp)
        .map(item => item.key)

      if (
        gwpKeys.length &&
        !this.getCartIsValid(eventState, freshPassedThreshold)
      ) {
        this.removeGwpKeysFromSections(eventState.sections, gwpKeys)
      }
    }

    const state =
      eventState != null && 'discount_codes' in eventState
        ? eventState
        : await window.BAO.Utils.fetchJSON('/cart.js')

    const cartIsValid = this.getCartIsValid(state, freshPassedThreshold)

    if (!cartIsValid) {
      await this.removeGwpItems(state)
    }

    if (cartIsValid && this.data?.autoAddVariantId && !this._autoAddInflight) {
      const hasGwp = state.items.some(item => item.properties?._gwp)
      if (!hasGwp) {
        this._autoAddInflight = true
        window.BAO.Cart.addItem({
          id: this.data.autoAddVariantId,
          quantity: 1,
          properties: { _gwp: 'true' },
        }).finally(() => {
          this._autoAddInflight = false
        })
        return
      }
    }

    if (!this.applyDiscountCodeAutomatically) return

    const alreadyHasDiscountCode = this.getAlreadyHasDiscountCode(state)

    if (alreadyHasDiscountCode && !cartIsValid) {
      return this.removeDiscountCode(state)
    } else if (cartIsValid && !alreadyHasDiscountCode) {
      return this.addDiscountCode(state)
    }
  }

  async addDiscountCode (state) {
    const finalState = await window.BAO.Cart.updateCartDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          code => code !== this.discountCode,
        ),
        this.discountCode,
      ].join(','),
      { suppressDiscountValidation: true },
    )

    this.renderPushCartSections(finalState)

    return finalState
  }

  async removeDiscountCode (state) {
    const finalState = await window.BAO.Cart.updateCartDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          code => code !== this.discountCode,
        ),
      ].join(','),
      { suppressDiscountValidation: true },
    )

    this.renderPushCartSections(finalState)

    return finalState
  }

  /**
   * Renders the pushCartSections HTML from a cart-mutation response into
   * the drawer.
   */
  renderPushCartSections (state) {
    if (!state?.sections) return

    const sr = window.BAO.sectionRenderer
    const sectionsToRender = sr.pushCartSections
      .filter(s => s.section in state.sections)
      .map(s => ({ ...s, html: state.sections[s.section] }))

    if (sectionsToRender.length) {
      sr.renderMultiple(sectionsToRender)
    }
  }

  removeGwpKeysFromSections (sections, gwpKeys) {
    for (const sectionKey of Object.keys(sections)) {
      const html = sections[sectionKey]
      if (!html) continue

      const dom = window.BAO.sectionRenderer.convertToDOM(html)
      let modified = false

      for (const key of gwpKeys) {
        const cartItemEl = window.BAO.Utils.getElement(
          `cart-item[data-key="${key}"]`,
          { context: dom },
        )
        if (!cartItemEl) continue

        const wrapper = cartItemEl.closest('.psh-CartItems_Item')
        ;(wrapper || cartItemEl).remove()
        modified = true
      }

      if (modified) {
        sections[sectionKey] = dom.body.innerHTML
      }
    }
  }

  async removeGwpItems (state) {
    const gwpItems = state.items.filter(item => item.properties?._gwp)

    if (gwpItems.length === 0) return

    let finalState
    for (const item of gwpItems) {
      finalState = await window.BAO.Cart.removeItem(item.key)
    }

    this.renderPushCartSections(finalState)
  }

  getPassedThresholdFromSections (sections) {
    for (const html of Object.values(sections)) {
      if (!html) continue
      const dom = window.BAO.sectionRenderer.convertToDOM(html)
      const el = window.BAO.Utils.getElement(
        'script[data-gift-with-purchase-data]',
        { context: dom },
      )
      if (el) {
        try {
          return JSON.parse(el.textContent.trim()).passedThreshold
        } catch {
          // ignore malformed JSON
        }
      }
    }
    return null
  }

  getCartIsValid (cart, freshPassedThreshold = null) {
    if (!cart?.items) return false

    const {
      discountType,
      itemType,
      thresholdAmount,
      thresholdQuantity,
      qualifyingProductIds,
      marketEnabled,
      customerEligible,
    } = this.data

    if (marketEnabled === false || customerEligible === false) return false

    if (itemType === 'Collection' || !qualifyingProductIds?.length) {
      return freshPassedThreshold !== null
        ? freshPassedThreshold
        : this.data.passedThreshold
    }

    const qualifyingItems = cart.items.filter(
      item =>
        !item.properties?._gwp && qualifyingProductIds.includes(item.product_id),
    )

    if (discountType === 'MinimumPurchase') {
      const total = qualifyingItems.reduce(
        (sum, item) => sum + item.final_line_price,
        0,
      )
      return total >= thresholdAmount
    }

    if (discountType === 'MinimumQuantity') {
      const qty = qualifyingItems.reduce((sum, item) => sum + item.quantity, 0)
      return qty >= thresholdQuantity
    }

    return this.data.passedThreshold
  }

  getAlreadyHasDiscountCode (cart) {
    return cart.discount_codes.some(
      discountCode => discountCode.code === this.discountCode,
    )
  }

  getCurrentAppliedDiscountCodes (cart) {
    return cart.discount_codes.map(discountCode => discountCode.code)
  }

  get applyDiscountCodeAutomatically () {
    return this.data?.applyCodeAutomatically ?? false
  }

  get discountCode () {
    return this.data?.discountCode ?? null
  }

  get dataElement () {
    return window.BAO.Utils.getElement('script[data-gift-with-purchase-data]')
  }

  get shouldRun () {
    return !!this.dataElement
  }

  get data () {
    try {
      return JSON.parse(this.dataElement.textContent.trim())
    } catch {
      return null
    }
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()

class GiftWithPurchaseItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['idInput']
  }

  setupListeners () {
    super.setupListeners()

    if (this.els['product-option']?.exists) {
      this.listeners.add(this, 'change', this.onFormChange.bind(this))
      this._updateAddBtn()
    }
    if (this.els.add?.exists) {
      this.listeners.add(
        this.els.add.element,
        'click',
        this._onAddClick.bind(this),
      )
    }
    if (this.els['option-label']?.exists) {
      this.listeners.add(this, 'click', this._onOptionLabelClick.bind(this))
    }
  }

  _onOptionLabelClick (e) {
    if (!e.target.matches(this.els['option-label'].selector)) return

    const select = this.querySelector(
      `#${CSS.escape(e.target.getAttribute('for'))}`,
    )
    if (!select) return

    e.preventDefault()

    if (typeof select.showPicker === 'function') {
      select.showPicker()
    } else {
      select.focus()
    }
  }

  _updateAddBtn () {
    if (!this.els.add?.exists) return
    this.els.add.element.disabled =
      !this.allValuesAreFilled || !this.currentCombinationIsAvailable
  }

  async onFormChange (e) {
    if (!e.target.matches(this.els['product-option'].selector)) return
    if (this._inflight) {
      await this._inflight
      return
    }
    this.setSelectsDisabled(true)
    this._inflight = this.handleFormChange(e).finally(() => {
      this._inflight = null
      this.setSelectsDisabled(false)
    })
    await this._inflight
  }

  async _onAddClick () {
    const variantId = parseInt(this.els.idInput.element.value, 10)
    if (!variantId || this._inflight) return

    const btn = this.els.add.element
    btn.disabled = true

    const existingKey = this.getAttribute('data-item-key')
    if (existingKey) {
      const removeState = await window.BAO.Cart.removeItem(existingKey)
      if (!removeState) {
        btn.disabled = false
        return
      }
    }

    await window.BAO.Cart.addItem({
      id: variantId,
      quantity: 1,
      properties: { _gwp: 'true' },
    })

    btn.disabled = false
  }

  setSelectsDisabled (disabled) {
    if (!this.els['product-option']?.exists) return
    for (const select of this.els['product-option'].elements) {
      select.disabled = disabled
    }
  }

  async handleFormChange (e) {
    const productOptionValues = this.els['product-option'].elements.map(
      option => {
        if (option.value === '') {
          return option.getAttribute('data-default-option-value-id')
        }
        const selectedOption = Array.from(option.options).find(
          o => o.value === option.value,
        )
        return selectedOption.getAttribute('data-option-id')
      },
    )

    const handle = encodeURIComponent(this.getAttribute('data-product-handle'))
    const optionValuesParam = encodeURIComponent(productOptionValues.join(','))
    const response = await window.BAO.sectionRenderer.fetchSingle(
      'product-gift-with-purchase-unclaimed',
      `/products/${handle}?option_values=${optionValuesParam}`,
    )
    const newSection = window.BAO.sectionRenderer.convertToDOM(response)
    const newProductOptions = window.BAO.Utils.getElements(
      this.els['product-option'].selector,
      { context: newSection },
    )

    for (const newProductOption of newProductOptions) {
      const currentProductOption = this.els['product-option'].elements.find(
        option =>
          option.getAttribute('name') === newProductOption.getAttribute('name'),
      )
      if (!currentProductOption) continue

      for (const currentOption of currentProductOption.options) {
        if (currentOption.value === '') continue
        const newOption = Array.from(newProductOption.options).find(
          o => o.value === currentOption.value,
        )
        if (!newOption) continue

        currentOption.setAttribute(
          'data-variant-id',
          newOption.getAttribute('data-variant-id'),
        )

        if (!currentOption.hasAttribute('data-perma-disabled')) {
          currentOption.disabled = newOption.disabled
        }
      }
    }

    this.applyAllowedVariantFilter()
    this.updateOptionNotes()
    this._updateAddBtn()

    if (!this.allValuesAreFilled) return
    if (!this.currentCombinationIsAvailable) return

    const changedSelect = e.target
    const selectedOption = Array.from(changedSelect.options).find(
      o => o.value === changedSelect.value,
    )
    const variantId = parseInt(
      selectedOption?.getAttribute('data-variant-id'),
      10,
    )
    if (!variantId) return

    this.setIdInputValue(variantId)

    if (!this.els.add?.exists) {
      const existingKey = this.getAttribute('data-item-key')
      if (existingKey) {
        const removeState = await window.BAO.Cart.removeItem(existingKey)
        if (!removeState) return
      }
      await window.BAO.Cart.addItem({
        id: variantId,
        quantity: 1,
        properties: { _gwp: 'true' },
      })
    }
  }

  setIdInputValue (value) {
    this.els.idInput.element.value = value
  }

  applyAllowedVariantFilter () {
    if (!this.els['product-option']?.exists) return
    const allowed = this.getAttribute('data-gwp-allowed-variant-ids')
    if (!allowed) return
    for (const select of this.els['product-option'].elements) {
      for (const option of select.options) {
        if (option.value === '') continue
        const variantId = option.getAttribute('data-variant-id')
        const needle = `,${variantId},`
        if (!allowed.includes(needle)) option.disabled = true
      }
    }
  }

  updateOptionNotes () {
    if (!this.els['product-option']?.exists) return
    const selects = this.els['product-option'].elements
    const notes = this.els['option-note']?.elements || []
    selects.forEach((select, index) => {
      const note = notes[index]
      if (!note) return
      const selected = Array.from(select.options).find(
        o => o.value === select.value,
      )
      const showNote = !!selected && selected.disabled && select.value !== ''
      note.hidden = !showNote
    })
  }

  get allValuesAreFilled () {
    if (!this.els['product-option']?.exists) return true
    return this.els['product-option'].elements.every(
      option => option.value !== '',
    )
  }

  get currentCombinationIsAvailable () {
    if (!this.els['product-option']?.exists) return true
    return this.els['product-option'].elements.every(select => {
      const selected = Array.from(select.options).find(
        o => o.value === select.value,
      )
      return selected && !selected.disabled
    })
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define('gift-with-purchase-item', GiftWithPurchaseItem)
}

class GiftWithPurchasePDP extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this._cartChangeHandler = this._onCartChange.bind(this)
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this._cartChangeHandler,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this._cartChangeHandler,
    )
  }

  async _onCartChange () {
    const sectionId = this.dataset.section
    if (!sectionId) return

    const html = await window.BAO.sectionRenderer.fetchSingle(sectionId)
    const dom = window.BAO.sectionRenderer.convertToDOM(html)
    const fresh = window.BAO.Utils.getElement('gift-with-purchase-pdp', {
      context: dom,
    })
    if (fresh) this.innerHTML = fresh.innerHTML
  }
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define('gift-with-purchase-pdp', GiftWithPurchasePDP)
}

const KeenSlider = window.KeenSlider

class GiftWithPurchaseCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    if (!this.els.slide?.exists) return

    const {
      setInitialised,
      useAdaptiveHeight,
      useArrows,
    } = window.BAO.Carousel.plugins

    this.carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        loop: false,
        mode: 'snap',
        slides: { perView: 1, spacing: 12 },
      },
      [
        setInitialised(),
        useAdaptiveHeight(),
        useArrows({
          next: this.els.next?.element,
          previous: this.els.previous?.element,
        }),
      ],
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.carousel?.destroy()
  }
}

if (!customElements.get('gift-with-purchase-carousel')) {
  customElements.define('gift-with-purchase-carousel', GiftWithPurchaseCarousel)
}
